/**
 * The HTTP client. Every backend call in the admin goes through here.
 *
 * SESSION MODEL (decision 2026-09-08 — see CLAUDE.md):
 * - The **access token** (30 min) lives in a module-local variable — memory only.
 *   It is never written to localStorage/sessionStorage, so an XSS payload cannot
 *   read a durable credential out of storage.
 * - The **refresh token** (120 days) is never visible to JavaScript at all. The
 *   backend sets it as an `httpOnly; Secure; SameSite` cookie; the browser attaches
 *   it automatically to the refresh endpoint because we send `credentials:
 *   "include"`. That is the whole reason we added the cookie-session endpoints
 *   rather than using the body-token `/admin/auth/login`.
 * - On a cold load there is no access token, so `bootstrapSession()` calls refresh
 *   once; the cookie decides whether we are still signed in.
 *
 * On 401 the request is retried exactly once behind a **single-flight** refresh:
 * concurrent 401s share one refresh promise, so a page firing six queries produces
 * one refresh, not six (which would fight over the rotating refresh token and log
 * the user out).
 */
import { API_URL, TENANT_ID } from "./config";
import { ApiError, NetworkError, type ProblemDetails } from "./errors";

// ─── Access token (memory only) ───────────────────────────────────────────────

let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export const getAccessToken = (): string | null => accessToken;
export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

/** Called when refresh fails, i.e. the session is genuinely over. */
export const setSessionLostHandler = (fn: (() => void) | null): void => {
  onSessionLost = fn;
};

// ─── Single-flight refresh ────────────────────────────────────────────────────

/**
 * Outcome of a refresh attempt:
 * - `ok`    — a new access token is in memory.
 * - `lost`  — the backend said the session is over (401/403). Sign out.
 * - `error` — transient: network failure or a 5xx/other status. The session may
 *             well still be valid, so the caller surfaces `error` and does NOT
 *             sign the operator out (a network blip must not end a desk shift).
 */
export type RefreshResult =
  | { kind: "ok" }
  | { kind: "lost" }
  | { kind: "error"; error: Error };

let refreshInFlight: Promise<RefreshResult> | null = null;

/**
 * Exchange the httpOnly refresh cookie for a new access token.
 * Concurrent callers share one request — the refresh token is single-use and
 * rotating, so parallel refreshes would invalidate each other.
 */
export function refreshSession(): Promise<RefreshResult> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async (): Promise<RefreshResult> => {
    try {
      let res: Response;
      try {
        res = await fetch(`${API_URL}/admin/auth/session/refresh`, {
          method: "POST",
          credentials: "include", // sends the httpOnly refresh cookie
          headers: { "X-Tenant-ID": TENANT_ID },
        });
      } catch (cause) {
        // Network failure is NOT a lost session — keep whatever token we have.
        return { kind: "error", error: new NetworkError(cause) };
      }
      if (res.status === 401 || res.status === 403) {
        accessToken = null;
        return { kind: "lost" };
      }
      if (!res.ok) {
        return {
          kind: "error",
          error: new ApiError(res.status, await parseProblem(res), res.headers.get("X-Trace-ID")),
        };
      }
      const body = (await res.json()) as { access_token?: string };
      if (!body.access_token) {
        return { kind: "error", error: new ApiError(res.status, { status: res.status }, null) };
      }
      accessToken = body.access_token;
      return { kind: "ok" };
    } catch (cause) {
      // e.g. an unparsable body from a proxy error page — transient, not a sign-out.
      return { kind: "error", error: cause instanceof Error ? cause : new NetworkError(cause) };
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Non-sensitive hint that a session cookie *probably* exists.
 *
 * The refresh cookie is httpOnly, so JavaScript cannot ask whether it is there.
 * Without a hint, every anonymous visit fires a refresh that is guaranteed to 401
 * and shows up as a console error. This flag is a boolean, not a credential —
 * forging it buys an attacker nothing, because the cookie is still what the
 * server checks.
 */
const SESSION_HINT_KEY = "rateslk.admin.session";

export function setSessionHint(active: boolean): void {
  try {
    if (active) localStorage.setItem(SESSION_HINT_KEY, "1");
    else localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    /* storage blocked — we simply lose the optimisation */
  }
}

function hasSessionHint(): boolean {
  try {
    return localStorage.getItem(SESSION_HINT_KEY) === "1";
  } catch {
    // Can't read storage: assume a session might exist and pay the one request.
    return true;
  }
}

/** Run once at startup: recover a session from the refresh cookie, if any. */
export async function bootstrapSession(): Promise<boolean> {
  if (!hasSessionHint()) return false;
  const result = await refreshSession();
  // Only forget the hint when the backend says the session is over — a transient
  // failure at cold load should still retry the cookie on the next reload.
  if (result.kind === "lost") setSessionHint(false);
  return result.kind === "ok";
}

// ─── Request ──────────────────────────────────────────────────────────────────

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /** JSON body. Omit for GET/DELETE. */
  body?: unknown;
  /** Query string values; `undefined`/`null`/`""` entries are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Skip the Authorization header + 401 retry (login/refresh only). */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(`${API_URL}${path.startsWith("/") ? path : `/${path}`}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function parseProblem(res: Response): Promise<ProblemDetails> {
  try {
    const text = await res.text();
    if (!text) return { status: res.status };
    return { status: res.status, ...(JSON.parse(text) as ProblemDetails) };
  } catch {
    return { status: res.status, detail: res.statusText };
  }
}

async function send(path: string, opts: RequestOptions, retry: boolean): Promise<Response> {
  const headers: Record<string, string> = { "X-Tenant-ID": TENANT_ID };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (!opts.anonymous && accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? "GET",
      credentials: "include",
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (cause) {
    if (opts.signal?.aborted) throw cause; // a cancelled query is not an error
    throw new NetworkError(cause);
  }

  // Access token expired → refresh once, then replay the original request.
  if (res.status === 401 && retry && !opts.anonymous) {
    const result = await refreshSession();
    if (result.kind === "ok") return send(path, opts, false);
    // Network blip / 5xx on refresh: surface the error, keep the session.
    if (result.kind === "error") throw result.error;
    accessToken = null;
    onSessionLost?.();
  }

  return res;
}

/** Perform a request and decode the JSON body. Throws `ApiError` on non-2xx. */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await send(path, opts, true);

  if (!res.ok) {
    throw new ApiError(res.status, await parseProblem(res), res.headers.get("X-Trace-ID"));
  }
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ─── Verbs ────────────────────────────────────────────────────────────────────

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<T>(path, { method: "GET", ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),

  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),

  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),

  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),

  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
